import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { SurveyResponseData, FormField } from '../features/surveys/types';
import { Icon } from '@iconify/react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { SortableHeaderIcon, PaginationControls, paginationPropsFromTable } from './table-shared';
import { SurveyResponseFeedbackPanel } from './SurveyResponseFeedbackPanel';

interface Props {
  data: SurveyResponseData[];
  loading: boolean;
  surveyId: string;
  onResponseUpdated?: () => void;
}

// Función para formatear valores de respuesta
const formatCellValue = (value: any, field?: FormField): React.ReactNode => {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground">-</span>;
  }

  if (field?.type === 'checkbox-multiple' && Array.isArray(value)) {
    return (
      <div className="space-y-1">
        {value.map((item) => (
          <div key={String(item)} className="flex items-center gap-2">
            <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
            <span className="text-sm">{String(item)}</span>
          </div>
        ))}
      </div>
    );
  }

  if (Array.isArray(value)) {
    return <span className="text-sm">{value.join(', ')}</span>;
  }

  if (typeof value === 'boolean') {
    return (
      <span className={`text-sm ${value ? 'text-green-600' : 'text-red-600'}`}>
        {value ? 'Sí' : 'No'}
      </span>
    );
  }

  if (typeof value === 'object') {
    return (
      <span className="text-xs font-mono text-muted-foreground">
        {JSON.stringify(value)}
      </span>
    );
  }

  const stringValue = String(value);
  // Truncar texto largo
  if (stringValue.length > 50) {
    return (
      <span className="text-sm" title={stringValue}>
        {stringValue.substring(0, 50)}...
      </span>
    );
  }

  return <span className="text-sm">{stringValue}</span>;
};

export function SurveyResponsesTable({ data, loading, surveyId, onResponseUpdated }: Props) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pageSize, setPageSize] = useState<number>(10);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [isMobile, setIsMobile] = useState(false);

  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Obtener todas las preguntas únicas de todas las respuestas
  const allFields = useMemo(() => {
    const fieldMap = new Map<string, FormField>();

    data.forEach((response) => {
      if (response.form?.structure?.fields) {
        response.form.structure.fields.forEach((field) => {
          if (!fieldMap.has(field.id)) {
            fieldMap.set(field.id, field);
          }
        });
      }
    });

    return Array.from(fieldMap.values());
  }, [data]);

  // Toggle de fila expandida
  const toggleRow = (rowId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const expandCellRenderer = useCallback(({ row }: any) => {
    const isExpanded = expandedRows.has(row.id);
    return (
      <button
        onClick={() => toggleRow(row.id)}
        className="p-1 hover:bg-muted rounded transition-colors"
        aria-label={isExpanded ? 'Colapsar' : 'Expandir'}
      >
        <Icon
          icon={isExpanded ? 'mdi:minus' : 'mdi:plus'}
          className="w-5 h-5 text-muted-foreground"
        />
      </button>
    );
  }, [expandedRows, toggleRow]);

  const expandColumn: ColumnDef<SurveyResponseData>[] = useMemo(() => [
    { id: 'expand', header: '', cell: expandCellRenderer, enableSorting: false, size: 50 },
  ], [expandCellRenderer]);

  const scoreCellRenderer = useCallback(
    (info: { row: { original: SurveyResponseData } }) => {
      const calc = info.row.original.calculation;
      if (!calc) return <span className="text-muted-foreground">N/A</span>;
      const below = info.row.original.belowThreshold;
      return (
        <span className={`text-sm font-medium ${below ? 'text-destructive' : ''}`}>
          {calc.totalScore.toFixed(2)}
        </span>
      );
    },
    [],
  );

  const scoreColumn: ColumnDef<SurveyResponseData>[] = useMemo(() => [
    { id: 'score', header: 'Puntaje', cell: scoreCellRenderer, enableSorting: false, size: 90 },
  ], [scoreCellRenderer]);

  const idCellRenderer = useCallback(
    (info: any) => <span className="font-mono text-xs">{String(info.getValue()).substring(0, 8)}...</span>,
    []
  );

  const idColumn: ColumnDef<SurveyResponseData>[] = useMemo(() => [
    { accessorKey: 'id', header: 'ID', cell: idCellRenderer, size: 120 },
  ], [idCellRenderer]);

  // Columnas dinámicas basadas en preguntas
  const questionColumns: ColumnDef<SurveyResponseData>[] = useMemo(() => {
    return allFields.map((field) => ({
      id: `field_${field.id}`,
      header: field.label,
      cell: (info) => {
        const response = info.row.original;
        const value = response.data[field.id];
        return formatCellValue(value, field);
      },
      size: 150,
    }));
  }, [allFields]);


  // Determinar qué columnas mostrar según el tamaño de pantalla
  const visibleColumns = useMemo(() => {
    if (!isMobile) {
      return [...expandColumn, ...idColumn, ...scoreColumn, ...questionColumns];
    }

    const firstQuestions = questionColumns.slice(0, 2);
    const restQuestions = questionColumns.slice(2);

    return {
      main: [...expandColumn, ...idColumn, ...scoreColumn, ...firstQuestions],
      hidden: restQuestions,
    };
  }, [isMobile, expandColumn, idColumn, scoreColumn, questionColumns]);

  const columns = isMobile 
    ? (visibleColumns as { main: ColumnDef<SurveyResponseData>[] }).main
    : (visibleColumns as ColumnDef<SurveyResponseData>[]);

  const table = useReactTable({
    data: data ?? [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  useEffect(() => {
    table.setPageSize(pageSize);
  }, [pageSize, table]);

  const hiddenColumns = isMobile 
    ? (visibleColumns as { hidden: ColumnDef<SurveyResponseData>[] }).hidden
    : [];

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto card">
        <table className="table w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={`${header.column.getCanSort() ? 'cursor-pointer select-none' : ''} px-3 py-2 text-left text-sm font-medium text-foreground`}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                  >
                    <span className="inline-flex items-center gap-1">
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <SortableHeaderIcon sortDirection={header.column.getIsSorted()} />
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td className="px-3 py-3 text-sm text-slate-600" colSpan={columns.length}>
                  Cargando...
                </td>
              </tr>
            )}
            {!loading && table.getRowModel().rows.length === 0 && (
              <tr>
                <td className="px-3 py-3 text-sm text-slate-600" colSpan={columns.length}>
                  No hay respuestas registradas
                </td>
              </tr>
            )}
            {!loading && table.getRowModel().rows.length > 0 && (
              <>
                {table.getRowModel().rows.map((row) => {
                  const isExpanded = expandedRows.has(row.id);
                  const response = row.original;
                  
                  return (
                    <React.Fragment key={row.id}>
                      <tr className={`hover:bg-muted/50 ${response.belowThreshold ? 'bg-destructive/5' : ''}`}>
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} className="px-3 py-2">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                      {isExpanded && (
                        <tr className="bg-muted/30">
                          <td colSpan={columns.length} className="px-3 py-3">
                            {isMobile && hiddenColumns.length > 0 && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                                {hiddenColumns.map((col) => {
                                  const fieldId = col.id?.replace('field_', '') ?? '';
                                  const field = allFields.find((f) => f.id === fieldId);
                                  const value = response.data[fieldId];

                                  return (
                                    <div key={col.id} className="border-b border-border pb-2">
                                      <div className="text-xs font-medium text-muted-foreground mb-1">
                                        {col.header as string}
                                      </div>
                                      <div className="text-sm">
                                        {formatCellValue(value, field)}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <SurveyResponseFeedbackPanel
                              surveyId={surveyId}
                              response={response}
                              onUpdated={onResponseUpdated}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </>
            )}
          </tbody>
        </table>
      </div>

      <PaginationControls {...paginationPropsFromTable(table, pageSize, [10, 20, 50], setPageSize)} />
    </div>
  );
}
